import type { JsonObject } from "@/api";
import { getAtPath } from "./recipeCoerce";
import { recipeValueAt } from "./recipeFlatten";
import type { OverridePair } from "./recipeTypes";

// config 를 고른 직후 override 행을 팀 관례 값으로 시드한다. training-infra Submit 의
// RECOMMENDED_OVERRIDE_VALUES·길이 form 동기화와 같은 어휘를 유지한다.

export const RECOMMENDED_OVERRIDE_VALUES: Readonly<Record<string, string>> = {
  "dataset.predecoded.enable": "true",
  "train.num_workers": "24",
  "train.num_val_workers": "12",
  "train.batch_size": "32",
};

export const BUDGET_UNIT_KEY = "train.budget.unit";
export const BUDGET_TOTAL_KEY = "train.budget.total";
export const NUM_EPOCHS_KEY = "train.num_epochs";
export const BUDGET_UNIT_OPTIONS = ["epoch", "step"] as const;
export const LR_OVERRIDE_KEY = "train.optimizer_params.default.lr";
export const BATCH_SIZE_OVERRIDE_KEY = "train.batch_size";
// 비전 인코더 입력 해상도 `[H, W]`. `image_converter.image_size` 와 status head 의 `target_size`
// 가 인터폴레이션으로 이 키를 따르므로, 해상도를 바꾸려면 이 키 하나만 바꾸면 된다.
// 데이터셋의 `observation_image` shape 과는 다른 축이다. 그쪽은 기준/추론 해상도이고, 이미지는
// 거기서 이 크기로 resize 된다.
export const IMAGE_SIZE_OVERRIDE_KEY = "policy.vision_encoder.params.image_size";

// config 의 현재 `[H, W]`. 인터폴레이션이거나 두 정수 목록이 아니면 null 이다. 그 경우 폼은 값을
// 만들지 않고 비워 둔다. 잘못 채우면 인코더 입력이 알림 없이 바뀐다.
export function imageSizeAt(data: JsonObject): { height: number; width: number } | null {
  const raw = getAtPath(data, IMAGE_SIZE_OVERRIDE_KEY);
  if (!Array.isArray(raw) || raw.length !== 2) {
    return null;
  }
  const [height, width] = raw;
  if (!Number.isInteger(height) || !Number.isInteger(width)) {
    return null;
  }
  return { height: height as number, width: width as number };
}

export function isBudgetForm(data: JsonObject): boolean {
  return getAtPath(data, "train.budget") !== undefined;
}

// key 의 기본값. 학습 길이 키는 config 현재 값을 먼저 쓴다(unit 은 "epoch" 폴백). 그 외는
// 추천값 → config 현재 값 → 빈 문자열 순서다.
export function overrideDefaultValue(key: string, data: JsonObject): string {
  if (key === BUDGET_UNIT_KEY) {
    return recipeValueAt(data, key) ?? "epoch";
  }
  if (key === BUDGET_TOTAL_KEY || key === NUM_EPOCHS_KEY) {
    return recipeValueAt(data, key) ?? "";
  }
  return RECOMMENDED_OVERRIDE_VALUES[key] ?? recipeValueAt(data, key) ?? "";
}

// 체크포인트 보존 노브 두 개. learning-core 에 "마지막만 저장" 스위치가 없어서 이 둘을 함께 설정해야
// 동작한다(`core/train.py` 의 저장 경로 셋 중 `checkpoints/last` 만 남긴다).
export const SAVE_TOP_K_KEY = "train.save_top_k";
export const SAVE_EVERY_N_EVALS_KEY = "train.budget.save_every_n_evals";

// ⚠️ 이 문법은 상류 두 곳의 사본이다. **고칠 때 반드시 두 곳을 함께 확인한다.**
//
//   1. learning-core `core/training_budget.py::_parse_count`. 실제 학습에 쓰는 기준 구현이다.
//      `^(\d+(?:\.\d+)?)([kmg]?)$` fullmatch, `_` 제거, `round()`.
//   2. training-infra `services/session_config_parse.py::_parse_count`. 진행률·ETA 표시용
//      사본이다. 마지막 글자를 접미사로 떼고 `int()` 로 소수를 버린다(반올림 아님).
//
// 세 구현이 다르면 화면이 계산한 값과 실제 학습이 쓰는 값이 어긋난다. 이 파일은 1번(기준 구현)을
// 따른다. 이 값은 제출 payload 로 전달되어 학습을 직접 바꾸므로 표시용 근사가 아니다.
const COUNT_RE = /^(\d+(?:\.\d+)?)([kmg]?)$/;
const COUNT_MULTIPLIERS: Readonly<Record<string, number>> = { k: 1e3, m: 1e6, g: 1e9 };

/**
 * 학습 길이 같은 count 값을 정수로 읽는다. learning-core `_parse_count` 와 같은 규칙이다.
 *
 * **`Number.parseInt` 를 쓰면 안 된다.** 실제 config 는 `30k`·`7.5k`·`150k` 형태를 쓴다
 * (uss1 실측 2026-08-11, 최근 90일 153건). `parseInt("30k")` 는 30, `parseInt("7.5k")` 는 7 을
 * 반환한다. 그 값으로 저장 주기를 계산하면 노브가 알림 없이 아무 효과도 내지 않는다.
 *
 * Args:
 *   raw: config 값 또는 override 행의 문자열.
 *
 * Returns:
 *   음이 아닌 정수. 문법에 맞지 않으면 `null`.
 */
export function parseCount(raw: string): number | null {
  const match = COUNT_RE.exec(raw.trim().toLowerCase().replace(/_/g, ""));
  if (match === null) {
    return null;
  }
  const [, number, suffix] = match;
  return Math.round(Number(number) * (suffix === "" ? 1 : COUNT_MULTIPLIERS[suffix]));
}

/**
 * "마지막 체크포인트만 남기기"가 쓸 저장 주기.
 *
 * 계획 태그 체크포인트 수는 `ceil(total_units / (eval_interval × save_every_n_evals))` 이다.
 * 마지막 unit 은 항상 저장되므로, 그 수를 1로 줄이면 마지막 하나만 남는다. `save_every_n_evals`
 * 는 필수·양수라 0·null 로 끌 수 없다. 그래서 값을 늘리는 것이 유일한 방법이다.
 *
 * 총량을 그대로 쓰면 두 모드 모두에서 충분하다.
 *   - epoch 모드: `eval_interval` 이 1 로 고정이다(step 전용 노브라 무시된다).
 *   - step 모드: `eval_interval >= 1` 이라 `total × eval_interval >= total` 이다.
 * 그래서 상류의 `eval_interval` auto 규칙(`round(total/100)`)을 복제하지 않아도 된다.
 *
 * Args:
 *   totalUnits: `train.budget.total`.
 *
 * Returns:
 *   저장 주기로 쓸 양의 정수. 총량을 읽을 수 없으면 `null` 이고, 그 경우 노브를 열지 않는다.
 */
export function saveEveryNEvalsForLastOnly(totalUnits: number | null): number | null {
  return totalUnits !== null && Number.isInteger(totalUnits) && totalUnits > 0 ? totalUnits : null;
}

// 추천 4종과 학습 길이 행을 시드한다. `train.budget` 이 있으면 unit/total 을,
// 없으면 `train.num_epochs` 를 쓴다.
//
// 추천 키는 config 가 선언한 키만 설정한다. 시드는 plain override 로 전달되는데, Hydra 는 없는 키의
// plain override 를 compose 에서 거절한다. `default_fine_tune` 처럼 checkpoint config 위에 겹치는
// 부분 config 는 네 키를 선언하지 않고, 그 값은 checkpoint 에서 상속된다.
export function seedOverrideRows(data: JsonObject): OverridePair[] {
  const recommendedKeys = Object.keys(RECOMMENDED_OVERRIDE_VALUES).filter(
    (key) => getAtPath(data, key) !== undefined,
  );
  const lengthKeys = isBudgetForm(data) ? [BUDGET_UNIT_KEY, BUDGET_TOTAL_KEY] : [NUM_EPOCHS_KEY];
  return [...recommendedKeys, ...lengthKeys].map((key) => ({
    key,
    value: overrideDefaultValue(key, data),
  }));
}
